import { HttpEvent, HttpEventType, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ApiService } from '@core/api/api.service';
import { EnvConfig } from '@core/config';
import { Observable, BehaviorSubject, Subscriber } from 'rxjs';

/** One SSE `data:` payload from `/chatbot/run-v2/stream`. */
export interface SmartAiAssistStreamEvent {
  type: 'session' | 'sources' | 'text' | 'answer' | 'done' | string;
  text?: string;
  answer?: string;
  question?: string;
  not_found?: boolean;
  session_id?: string;
  ch_id?: number | null;
  sources?: unknown[];
  context?: Record<string, unknown>;
  result?: {
    ch_id?: number;
    qa_id?: number;
    answer?: string;
    evidence_snippet?: string | null;
    snippets?: unknown[];
    ai_reasoning?: string;
    is_snippet_matched?: boolean;
    [key: string]: unknown;
  };
}

@Injectable({
  providedIn: 'root'
})
export class DueDiligenceService {
  // ================================
  // 📌 GET BEHAVIOR SUBJECT
  // ================================
  private behaviorSubject = new BehaviorSubject<any>(null);
  public behaviorSubject$ = this.behaviorSubject.asObservable();

  private ddBehaviorSubject = new BehaviorSubject<boolean>(false);
  public ddBehaviorSubject$ = this.ddBehaviorSubject.asObservable();

  private isEditBehaviorSubject = new BehaviorSubject<string>("Y");
  public isEditBehaviorSubject$ = this.isEditBehaviorSubject.asObservable();

  private selectedDD = new BehaviorSubject<string | null>(null);
  public selectedDD$ = this.selectedDD.asObservable();


  constructor(
    private apiService: ApiService,
    private env: EnvConfig
  ) {
  }

  private get baseUrl(): string {
    return this.env.aiEngineApiUrl || '';
  }

  private getHeaders() {
    return {
      headers: new HttpHeaders({
        'accept': 'application/json',
        'Content-Type': 'application/json'
      })
    };
  }

  // ================================
  // 📌 SET BEHAVIOR SUBJECT VALUE
  // ================================
  public setBehaviorSubjectValue(value: any): void {

    this.behaviorSubject.next(value);
  }

  // ================================
  // 📌 GET BEHAVIOR SUBJECT VALUE
  // ================================
  public getBehaviorSubjectValue(): any {
    return this.behaviorSubject.getValue();
  }

  // ================================
  // 📌 GET PROCESS ANSWERS
  // ================================
  public getProcessAnswerData(payload: any): Observable<any> {
    return this.apiService.post(this.baseUrl + "/get_process_answer_data", payload, this.getHeaders());
  }

  // ================================
  // 📌 UPDATE PROCESS ANSWER
  // ================================
  public updateProcessProcessanswerAnswer(payload: any): Observable<any> {
    return this.apiService.post(this.baseUrl + "/update_process_processanswer_answer", payload, this.getHeaders());
  }

  // ================================
  // 📌 UPDATE PROCESS ANSWER STATUS
  // ================================
  public updateProcessProcessanswerStatus(payload: any): Observable<any> {
    return this.apiService.post(this.baseUrl + "/update_process_processanswer_status", payload, this.getHeaders());
  }

  // ================================
  // 📌 UPDATE PROCESS SNIPPET LIKE/UNLIKE
  // ================================
  public updateProcesssnippetLikeUnlike(payload: any): Observable<any> {
    return this.apiService.post(this.baseUrl + "/update_processsnippet_like_unlike", payload, this.getHeaders());
  }

  // ================================
  // 📌 PROCESS ANSWER HISTORY
  // ================================
  public getProcessanswerhistoryByProcessanswerId(payload: any): Observable<any> {
    return this.apiService.post(this.baseUrl + "/get_processanswerhistory_by_processanswer_id", payload, this.getHeaders());
  }

  // ================================
  // 📌 PROCESS INSERT ANSWER IMAGES
  // ================================
  public insertProcessanswerimages(payload: any): Observable<any> {
    return this.apiService.post(this.baseUrl + "/extract_document_bytes", payload);
  }

  // ================================
  // 📌 DELETE ANSWER IMAGES
  // ================================
  public deleteProcessanswerimages(payload: any): Observable<any> {
    return this.apiService.post(this.baseUrl + "/delete_processanswerimages", payload, this.getHeaders());
  }

  // ================================
  // 📌 GET PROCESSQUEUE
  // ================================
  public getProcessqueueDataByProductId(payload: any): Observable<any> {
    return this.apiService.post(this.baseUrl + "/get_processqueue_data_by_product_id", payload, this.getHeaders());
  }

  // ================================
  // 📌 UPDATE PROCESSQUEUE STATUS (Cancel / etc.)
  // ================================
  public updateProcessProcessqueueStatus(payload: any): Observable<any> {
    return this.apiService.post(this.baseUrl + "/update_process_processqueue_status", payload, this.getHeaders());
  }

  // ================================
  // 📌 COPY PROCESSQUEUE DATA
  // ================================
  public copyProcessqueueData(payload: any): Observable<any> {
    return this.apiService.post(this.baseUrl + "/sp_copy_processqueue_data", payload, this.getHeaders());
  }

  // ================================
  // 📌 GET PROCESS COMMENTS
  // ================================
  getProcessComments(processAnswerId: string): Observable<any> {
    const payload = { processanswer_id: processAnswerId };
    const url = `${this.baseUrl}/get_processcomments`;

    return this.apiService.post(url, payload, this.getHeaders());
  }

  // ================================
  // 📌 GET PROCESS SNIPPETS
  // ================================
  getProcessSnippets(processAnswerId: string): Observable<any> {
    const payload = { processanswer_id: processAnswerId };
    const url = `${this.baseUrl}/get_processsnippet`;

    return this.apiService.post(url, payload, this.getHeaders());
  }

  // ================================
  // 📌 DELETE PROCESS COMMENT
  // ================================
  deleteProcessComment(commentId: number, userUid: any | null = null): Observable<any> {
    const payload = {
      comment_id: String(commentId),
      user_uid: userUid
    };
    const url = `${this.baseUrl}/delete_processcomment`;

    return this.apiService.post(url, payload, this.getHeaders());
  }

  // ================================
  // 📌 DELETE PROCESS SNIPPET
  // ================================
  deleteProcessSnippet(snippetId: number, userUid: string | null = null): Observable<any> {
    const payload = {
      snippet_id: snippetId,
      user_uid: userUid
    };
    const url = `${this.baseUrl}/delete_processsnippet`;

    return this.apiService.post(url, payload, this.getHeaders());
  }

  // ================================
  // 📌 INSERT PROCESS COMMENTS
  // ================================
  insertProcessComments(
    processAnswerId: string,
    createdByUid: string,
    comments: { comment: string }[]
  ): Observable<any> {
    const payload = {
      processanswer_id: processAnswerId,
      created_by_uid: createdByUid,
      comments
    };
    const url = `${this.baseUrl}/insert_processcomments`;

    return this.apiService.post(url, payload, this.getHeaders());
  }

  // ================================
  // 📌 GET RUN QUESTIONS
  // ================================
  getRunQuestions(payload: any): Observable<any> {
    const url = `${this.baseUrl}/get-run-ques`;
    return this.apiService.post(url, payload, this.getHeaders());
  }

  // ================================
  // 📌 GET RUN QUESTIONS
  // ================================
  getRunQuestionsBackground(payload: any): Observable<any> {
    const url = `${this.baseUrl}/get-run-ques-background`;
    return this.apiService.post(url, payload, this.getHeaders());
  }

  // ================================
  // 📌 GET DDQ RUN QUESTIONS 
  // ================================
  getDDQRunQuestionsBackground(payload: any): Observable<any> {
    const url = `${this.baseUrl}/get-run-ques-background-v2`;
    return this.apiService.post(url, payload, this.getHeaders());
  }

  // ================================
  // 📌 GET IM RUN QUESTIONS
  // ================================
  getIMRunQuestionsBackground(payload: any): Observable<any> {
    const url = `${this.baseUrl}/get-run-ques-background-v2`;
    return this.apiService.post(url, payload, this.getHeaders());
  }

  // ================================
  // 📌 GET AI JOB DETAILS
  // ================================
  getFundNameSuggestions(payload: {
    product_id: string;
    fund_name: string;
    firm_name?: string;
    created_by_uid?: string;
    count?: number;
  }): Observable<any> {
    const url = `${this.baseUrl}/generate_fund_name_suggestions`;
    return this.apiService.post(url, payload, this.getHeaders());
  }

  getFundName(payload: {
    vs_id: string;
    product_id: string;
  }): Observable<any> {
    const url = `${this.baseUrl}/get-fund-name`;
    return this.apiService.post(url, payload, this.getHeaders());
  }

  updateProductSelectedFundNameByUser(payload: {
    product_uid: string;
    selected_fund_name: string;
    user_uid: string | null;
  }): Observable<any> {
    const url = `${this.baseUrl}/update_product_selected_fund_name_by_user`;
    return this.apiService.post(url, payload, this.getHeaders());
  }

  getAiJobDetails(payload: any): Observable<any> {
    const url = `${this.baseUrl}/get_ai_job_details`;
    return this.apiService.post(url, payload, this.getHeaders());
  }

  // ================================
  // 📌 INSERT PROCESS QUEUE DATA
  // ================================
  insertProcessQueueData(payload: {
    template_type: string;
    product_id: string;
    created_by_uid: string;
    is_analyst: string;
    templatedetails_id?: number | string | null;
    userType?: string | null;
  }): Observable<any> {
    const url = `${this.baseUrl}/insert-processqueuedata`;
    return this.apiService.post(url, payload, this.getHeaders());
  }

  // ================================
  // 📌 SET BEHAVIOR SUBJECT VALUE FOR DD
  // ================================
  public setDDBehaviorSubjectValue(value: any): void {
    this.ddBehaviorSubject.next(value);
  }

  // ================================
  // 📌 GET ATTACHMENT IMAGE
  // ================================
  public getProcessanswerimagesById(payload: any): Observable<any> {
    return this.apiService.post(this.baseUrl + "/get_processanswerimages_by_id", payload, this.getHeaders());
  }

  // ================================
  // 📌 DOWNLOAD FILE
  // ================================
  public downloadFile(
    emittentId: string,
    productId: string,
    docId: string
  ): Observable<Blob> {

    const url =
      `${this.baseUrl}/files/download` +
      `?emittent_id=${emittentId}` +
      `&product_id=${productId}` +
      `&doc_id=${docId}`;

    return this.apiService.get(url, {
      responseType: 'blob',
      withCredentials: true
    });
  }

  // ================================
  // 📌 SET IS EDIT BEHAVIOR SUBJECT VALUE
  // ================================
  public setIsEditBehaviorSubjectValue(value: any): void {
    this.isEditBehaviorSubject.next(value);
  }

  // ================================
  // 📌 UPDATE DD APPROVAL DATA
  // ================================
  public updateDdApprovalData(payload: any): Observable<any> {
    return this.apiService.post(this.baseUrl + "/update_dd_approval_data", payload, this.getHeaders());
  }

  // ================================
  // 📌 SET Current Processqueue Id BEHAVIOR SUBJECT VALUE
  // ================================
  public setSelectedDD(value: any): void {
    this.selectedDD.next(value);
  }

  // ================================
  // 📌 DELETE PROCESS QUEUE DATA BY ID
  // ================================
  public deleteProcessQueueDataById(payload: { processqueue_id: string | number }): Observable<any> {
    const url = `${this.baseUrl}/delete_process_queue_data_by_id`;
    return this.apiService.post(url, payload, this.getHeaders());
  }

  public getTemplateQuestionsIncompleteStatusDataInJson(payload: {
    processqueue_id: number | string;
    created_by_uid: string;
  }): Observable<any> {
    const url = `${this.baseUrl}/get_template_questions_incomplete_status_data_in_json`;
    return this.apiService.post(url, payload, this.getHeaders());
  }
  // ================================
  // 📌 GET ACTIVE QUESTIONS JSON
  // ================================
  public getActiveQuestionsJson(payload: {} = {}): Observable<any> {
    const url = `${this.baseUrl}/sp_get_active_questions_json`;
    return this.apiService.post(url, payload, this.getHeaders());
  }

  // ================================
  // 📌 INSERT TEMPLATE QUESTION METADATA FROM JSON
  // ================================
  public insertTemplateQuestionMetadataFromJson(payload: {
    created_by_uid: string | null;
    templatedetails_id: number;
    document_name: string;
    document_path: string;
    questions: Array<{
      document_category_id: number;
      secondary_doc_category_id: number;
      section: string;
      sub_section: string | null;
      question_name: string;
      answer_type: string | null;
      answer_type_list: string[] | null;
      tag_topic: string | null;
      question_keywords: string | null;
      functional_description: string | null;
      is_knockout: string;
      is_manually_add?: string;
      knockout_criteria: Record<string, unknown> | null;
      suggested_match_json?: Array<{ question_text: string; is_selected: string }>;
      recommended_question_json?: Array<{ confidence: number; question_text: string }> | null;
    }>;
  }): Observable<{ question_doc_id: number; templatedetails_id: number; templatequestion_ids: number[]; total_questions_inserted: number }> {
    const url = `${this.baseUrl}/sp_insert_template_question_metadata_from_json`;
    return this.apiService.post(url, payload, this.getHeaders());
  }

  public deleteAllProcessDataByProcessqueueId(payload: {
    "dd_processqueue_id": any,
    "im_processqueue_id": any,
    "user_uid": any
  }): Observable<any> {
    const url = `${this.baseUrl}/delete_all_process_data_by_processqueue_id`;
    return this.apiService.post(url, payload, this.getHeaders());
  }

  // ================================
  // 📌 SMART AI ASSIST (Ask / Save Q&A)
  // ================================
  public submitSmartAiAssistQuestion(payload: any): Observable<any> {
    // Backend route name TBD; keep it isolated here.
    const url = `${this.baseUrl}/chatbot/run`;
    return this.apiService.post(url, payload, this.getHeaders());
  }

  // ================================
  // 📌 SMART AI ASSIST V2 (streaming)
  // ================================
  public submitSmartAiAssistQuestionV2(payload: {
    ch_id: number | null;
    created_by_uid: string | null;
    processqueue_id: string;
    product_id: string;
    questions: Array<{ question_name: string }>;
    fund_name: string;
    firm_name: string;
    vs_id: string;
    text_doc_urls: Record<string, string> | null;
  }): Observable<SmartAiAssistStreamEvent> {
    const url = `${this.baseUrl}/chatbot/run-v2/stream`;

    return new Observable<SmartAiAssistStreamEvent>((subscriber: Subscriber<SmartAiAssistStreamEvent>) => {
      let buffer = '';
      let lastPartialText = '';

      const emitFromBuffer = (flushRemainder: boolean): void => {
        const lines = buffer.split(/\r?\n/);
        if (!flushRemainder) {
          buffer = lines.pop() ?? '';
        } else {
          buffer = '';
        }

        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed.startsWith('data:')) {
            continue;
          }
          const raw = trimmed.slice(5).trim();
          if (!raw || raw === '[DONE]') {
            continue;
          }
          try {
            const parsed = JSON.parse(raw) as SmartAiAssistStreamEvent;
            subscriber.next(parsed);
          } catch {
            // Ignore malformed SSE chunks; keep reading the stream.
          }
        }
      };

      const subscription = this.apiService
        .post(url, payload, {
          headers: new HttpHeaders({
            Accept: 'text/event-stream',
            'Content-Type': 'application/json',
          }),
          observe: 'events',
          responseType: 'text',
          reportProgress: true,
        })
        .subscribe({
          next: (event: HttpEvent<string>) => {
            if (event.type === HttpEventType.DownloadProgress) {
              const partialText = (event as { partialText?: string }).partialText ?? '';
              const delta = partialText.slice(lastPartialText.length);
              lastPartialText = partialText;
              if (delta) {
                buffer += delta;
                emitFromBuffer(false);
              }
              return;
            }

            if (event.type === HttpEventType.Response) {
              const body = (event as HttpResponse<string>).body ?? '';
              if (!lastPartialText) {
                buffer = body;
              } else if (body.length > lastPartialText.length) {
                buffer += body.slice(lastPartialText.length);
              }
              emitFromBuffer(true);
              subscriber.complete();
            }
          },
          error: (err: unknown) => subscriber.error(err),
          complete: () => {
            if (!subscriber.closed) {
              emitFromBuffer(true);
              subscriber.complete();
            }
          },
        });

      return () => subscription.unsubscribe();
    });
  }

  public chGetChatHistoryHeaderData(payload: {
    processqueue_id: number;
    product_uid: string;
    user_id: string;
  }): Observable<any> {
    const url = `${this.baseUrl}/ch_get_chat_history_header_data`;
    return this.apiService.post(url, payload, this.getHeaders());
  }

  public chUpdateChatHistoryHeaderName(payload: { ch_id: number; ch_name: string }): Observable<any> {
    const url = `${this.baseUrl}/ch_update_chat_history_header_name`;
    return this.apiService.post(url, payload, this.getHeaders());
  }

  public chDeleteChatAllData(payload: { ch_id: number }): Observable<any> {
    const url = `${this.baseUrl}/ch_delete_chat_all_data`;
    return this.apiService.post(url, payload, this.getHeaders());
  }

  public chGetChatQuestionsData(payload: {
    ch_id: number;
  }): Observable<any> {
    const url = `${this.baseUrl}/ch_get_chat_questions_data`;
    return this.apiService.post(url, payload, this.getHeaders());
  }

  public chGetChatSnippetsData(payload: { qa_id: number }): Observable<any> {
    const url = `${this.baseUrl}/ch_get_chat_snippets_data`;
    return this.apiService.post(url, payload, this.getHeaders());
  }

  // ================================
  // 📌 SP GET QUESTION TAG TOPIC DDL
  // ================================
  public spGetQuestionTagTopicDdl(payload: {} = {}): Observable<any> {
    const url = `${this.baseUrl}/sp_get_question_tag_topic_ddl`;
    return this.apiService.post(url, payload, this.getHeaders());
  }

  // ================================
  // 📌 SP GET QUESTION KEYWORDS DDL
  // ================================
  public spGetQuestionKeywordsDdl(payload: {} = {}): Observable<any> {
    const url = `${this.baseUrl}/sp_get_question_keywords_ddl`;
    return this.apiService.post(url, payload, this.getHeaders());
  }


}

